import React from 'react';

import { FlexBox } from 'ml-rubiks-styleguide/src/box';
import { CircularProgress } from 'ml-rubiks-styleguide/src/circular-progress';
import { IconKeyboardArrowRight } from 'ml-rubiks-styleguide/src/icon';
import { List, ListItemProps } from 'ml-rubiks-styleguide/src/list';
import { Paper } from 'ml-rubiks-styleguide/src/paper';
import { Typography } from 'ml-rubiks-styleguide/src/typography';

import { getPaymentMethodIcon } from '../../constants/payment-method-config';
import { useI18n } from '../../contexts/i18n-context';
import {
    AvailablePaymentMethodsV1Dto,
    PaymentMethod,
    PaymentMethodTypeSelectorProps
} from '../../types';
import {
    getPaymentMethodTypeDescription,
    getPaymentMethodTypeName
} from '../../utils/payment-methods';
import { LoadingMessage } from '../common';

interface Props extends PaymentMethodTypeSelectorProps {
    availablePaymentMethods?: AvailablePaymentMethodsV1Dto[];
    isLoadingAvailableMethods?: boolean;
    hasLoadedAvailableMethods?: boolean;
    hasAvailableMethodsError?: boolean;
    loadingMethodType?: PaymentMethod | null;
}

export const PaymentMethodTypeSelector: React.FC<Props> = ({
    onTypeSelect,
    availablePaymentMethods = [],
    isLoadingAvailableMethods = false,
    hasLoadedAvailableMethods = false,
    hasAvailableMethodsError = false,
    loadingMethodType = null
}) => {
    const { t } = useI18n();

    const availableMethods = availablePaymentMethods;
    const isLoading = isLoadingAvailableMethods && !hasAvailableMethodsError;
    const error =
        hasAvailableMethodsError ||
        (hasLoadedAvailableMethods && availablePaymentMethods.length === 0);

    const paymentMethodTypes = availableMethods.flatMap(
        (providerData) =>
            providerData.paymentMethods?.map((method) => {
                const paymentMethodType = method.paymentMethod;
                const IconComponent = getPaymentMethodIcon(paymentMethodType);
                return {
                    type: paymentMethodType,
                    name: getPaymentMethodTypeName(paymentMethodType, t),
                    description: getPaymentMethodTypeDescription(
                        paymentMethodType,
                        t
                    ),
                    icon: IconComponent
                };
            }) || []
    );

    if (isLoading) {
        return (
            <FlexBox sx={{ py: 4 }}>
                <LoadingMessage
                    message={t(
                        'upc.loading.payment.methods',
                        'Loading payment methods...'
                    )}
                />
            </FlexBox>
        );
    }

    if (error) {
        return;
    }

    return (
        <Paper elevation="S">
            <List
                disablePadding
                items={paymentMethodTypes.map((option, index) => {
                    const isLoadingThisMethod =
                        loadingMethodType === option.type;
                    return {
                        sx: {
                            height: 68,
                            opacity: isLoadingThisMethod ? 0.7 : 1,
                            pointerEvents: isLoadingThisMethod ? 'none' : 'auto'
                        },
                        primary: (
                            <FlexBox
                                component="span"
                                gap={2}
                                vAlign="center"
                                sx={{ width: '100%' }}
                            >
                                <FlexBox sx={{ minWidth: 35 }}>
                                    {option.icon}
                                </FlexBox>
                                {option.name && (
                                    <Typography>
                                        {option.name}
                                    </Typography>
                                )}
                            </FlexBox>
                        ),
                        primaryRight: (
                            <FlexBox hAlign="flex-end">
                                {isLoadingThisMethod ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    <IconKeyboardArrowRight color="text.secondary" />
                                )}
                            </FlexBox>
                        ),
                        divider:
                            index === paymentMethodTypes.length - 1
                                ? undefined
                                : 'bottom',
                        textSlotProps: {
                            primary: {
                                sx: { fontWeight: 700 }
                            },
                            secondary: {
                                color: 'text.secondary'
                            }
                        },
                        onClick: isLoadingThisMethod
                            ? undefined
                            : () => onTypeSelect(option.type)
                    } satisfies ListItemProps<unknown>;
                })}
            />
        </Paper>
    );
};
